import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Stack,
  Chip,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { personsAPI } from '../../services/api';

export interface ParentCompanyItem {
  node: { id?: string; type?: string; label?: string; jurisdiction?: string; profilePhotoUrl?: string };
  edge: { id?: string; label?: string; data?: { sharesHeld?: number } };
}

interface CompanyShareholdersSectionProps {
  parentCompanies: ParentCompanyItem[];
  onEditShareholder: (params: { ownerId: string; isPerson: boolean; label: string }) => void;
  onRemoveShareholder: (params: { ownerId: string; isPerson: boolean; label: string }) => void;
}

const CompanyShareholdersSection: React.FC<CompanyShareholdersSectionProps> = ({
  parentCompanies,
  onEditShareholder,
  onRemoveShareholder,
}) => (
  <Card sx={{ borderRadius: 2 }}>
    <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PeopleIcon color="primary" />
        Shareholders
        <Chip label={parentCompanies.length} size="small" color="primary" />
      </Typography>
      <Divider sx={{ mb: 2 }} />
      {parentCompanies.length === 0 ? (
        <Alert severity="info">No parent owners found.</Alert>
      ) : (
        <List disablePadding>
          {parentCompanies.map(({ node, edge }, idx: number) => {
            const isPerson = String(node?.type) === 'person';
            const personId = isPerson ? String(node?.id).replace(/^person-/, '') : '';
            const ownerId = isPerson ? personId : String(node?.id);
            const label = edge?.label ? String(edge.label) : '';
            const pct = label.endsWith('%') ? label : '';
            const sharesNumber =
              typeof edge?.data?.sharesHeld === 'number' ? Number(edge.data.sharesHeld) : undefined;
            const sharesText = !pct && label ? label : '';
            const sharesDisplay =
              typeof sharesNumber === 'number'
                ? `${sharesNumber.toLocaleString()} shares`
                : sharesText
                  ? sharesText
                  : '';

            const percentMatch = pct.match(/(\d+(?:\.\d+)?)/);
            const percentage = percentMatch ? parseFloat(percentMatch[1]) : null;
            let chipColor = '#9e9e9e';
            let levelLabel = '';
            if (percentage !== null) {
              if (percentage >= 50) {
                chipColor = '#d32f2f';
                levelLabel = 'Majority';
              } else if (percentage >= 25) {
                chipColor = '#f57c00';
                levelLabel = 'Significant';
              } else if (percentage >= 10) {
                chipColor = '#1976d2';
                levelLabel = 'Moderate';
              } else {
                chipColor = '#388e3c';
                levelLabel = 'Minor';
              }
            }

            const initials = isPerson
              ? (node?.label || '')
                  .split(' ')
                  .map((n: string) => n[0])
                  .join('')
                  .slice(0, 2)
                  .toUpperCase() || '?'
              : (node?.label || '').slice(0, 2).toUpperCase() || '??';

            return (
              <React.Fragment key={edge.id ?? idx}>
                {idx > 0 && <Divider />}
                <Box display="flex" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={{ xs: 0.5, sm: 0 }}>
                  <ListItemButton
                    component={RouterLink}
                    to={
                      isPerson
                        ? `/persons/${encodeURIComponent(personId)}`
                        : `/companies/${encodeURIComponent(node?.id ?? '')}`
                    }
                    sx={{ flex: 1, px: 0, minWidth: 0, alignItems: 'flex-start', py: { xs: 1.25, sm: 1 } }}
                  >
                    <Avatar
                      src={
                        isPerson && node?.profilePhotoUrl && personId
                          ? personsAPI.getProfilePhotoUrl(personId)
                          : undefined
                      }
                      sx={{
                        width: 36,
                        height: 36,
                        mr: 2,
                        bgcolor: isPerson ? 'primary.main' : 'secondary.main',
                        fontSize: '0.875rem',
                      }}
                    >
                      {isPerson ? initials : <BusinessIcon />}
                    </Avatar>
                    <ListItemText
                      disableTypography
                      sx={{ minWidth: 0, mr: 1 }}
                      primary={
                        <Typography variant="body2" fontWeight="medium" sx={{ overflowWrap: 'anywhere' }}>
                          {node?.label}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ overflowWrap: 'anywhere' }}>
                            {isPerson ? 'Person' : `Company • ${node?.jurisdiction || ''}`}
                          </Typography>
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                            {pct && (
                              <>
                                <Chip
                                  label={pct}
                                  size="small"
                                  sx={{ bgcolor: chipColor, color: 'white', fontWeight: 'bold' }}
                                />
                                {levelLabel && (
                                  <Chip
                                    label={levelLabel}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderColor: chipColor,
                                      color: chipColor,
                                      bgcolor: `${chipColor}0d`,
                                    }}
                                  />
                                )}
                              </>
                            )}
                            {sharesDisplay && (
                              <Chip
                                label={sharesDisplay}
                                size="small"
                                variant="outlined"
                                sx={{ color: 'text.secondary' }}
                              />
                            )}
                          </Stack>
                        </Box>
                      }
                    />
                  </ListItemButton>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.5} sx={{ pt: { xs: 1, sm: 0 } }}>
                  <Tooltip title="Edit shareholder type">
                    <IconButton
                      aria-label="Edit shareholder"
                      size="small"
                      color="primary"
                      sx={{ minWidth: 40, minHeight: 40 }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onEditShareholder({
                          ownerId,
                          isPerson,
                          label: node?.label ?? '',
                        });
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    aria-label="Remove shareholder"
                    size="small"
                    color="error"
                    sx={{ minWidth: 40, minHeight: 40 }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onRemoveShareholder({
                        ownerId,
                        isPerson,
                        label: node?.label ?? '',
                      });
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                  </Stack>
                </Box>
              </React.Fragment>
            );
          })}
        </List>
      )}
    </CardContent>
  </Card>
);

export default CompanyShareholdersSection;
